import { LabelsSelector } from "../../store/selectors/LabelsSelector";
import { store } from "../../index";
import {
  updateActiveImageIndex,
  updateActiveLabelId,
  updateHighlightedLabelId,
  updateActiveLabelNameId,
  updateImageDataById,
} from "../../store/labels/actionCreators";
import { ViewPortActions } from "./ViewPortActions";
import { EditorActions } from "./EditorActions";
import { LabelType } from "../../data/enums/LabelType";
import { v4 as uuidv4 } from 'uuid';
import {
  ImageData,
  LabelLine,
  LabelPoint,
  LabelPolygon,
  LabelPolyline,
  LabelRect,
} from "../../store/labels/types";
import { LabelStatus } from "../../data/enums/LabelStatus";

export class ImageActions {
  public static getPreviousImage(): void {
    const currentImageIndex: number = LabelsSelector.getActiveImageIndex();
    ImageActions.getImageByIndex(currentImageIndex - 1);
  }

  public static getNextImage(): void {
    const currentImageIndex: number = LabelsSelector.getActiveImageIndex();
    ImageActions.getImageByIndex(currentImageIndex + 1);
  }

  public static getImageByIndex(index: number): void {
    const imageCount: number = LabelsSelector.getImagesData().length;

    if (index < 0 || index > imageCount - 1) {
      return;
    }

    EditorActions.setViewPortActionsDisabledStatus(false);
    ViewPortActions.setZoom(1);
    store.dispatch(updateActiveImageIndex(index));
    store.dispatch(updateActiveLabelId(null));
    store.dispatch(updateHighlightedLabelId(null));
  }
  private static getTagIdOnIndex(index) {
    const labelNames = LabelsSelector.getLabelNames()
    if (labelNames.length === 0 || index >= labelNames.length) return 0
    return labelNames[index].id
  }

  private static resolveActiveLabelType(): LabelType | null {
    const activeType = LabelsSelector.getActiveLabelType();
    if (activeType === LabelType.TAG) {
      return LabelType.TAG;
    }

    const activeLabelId = LabelsSelector.getActiveLabelId();
    if (!activeLabelId) return null;

    if (LabelsSelector.getActiveRectLabel()) return LabelType.RECT;
    if (LabelsSelector.getActivePolygonLabel()) return LabelType.POLYGON;
    if (LabelsSelector.getActivePolylineLabel()) return LabelType.POLYLINE;
    if (LabelsSelector.getActivePointLabel()) return LabelType.POINT;
    if (LabelsSelector.getActiveLineLabel()) return LabelType.LINE;

    return null;
  }

  public static setActiveLabelOnActiveImage(labelIndex: number): void {
    const labelNames = LabelsSelector.getLabelNames();
    if (labelNames.length < labelIndex + 1) {
      return;
    }

    const labelType = ImageActions.resolveActiveLabelType();
    if (!labelType) {
      return;
    }

    const imageData: ImageData = LabelsSelector.getActiveImageData();
    if (!imageData) {
      return;
    }

    store.dispatch(
      updateImageDataById(
        imageData.image_id,
        ImageActions.mapimageData(imageData, labelIndex, labelType)
      )
    );
    store.dispatch(updateActiveLabelNameId(labelNames[labelIndex].id));
  }

  private static mapimageData(
    imageData: ImageData,
    labelIndex: number,
    labelType: LabelType = LabelsSelector.getActiveLabelType()
  ): ImageData {
    const labelNames = LabelsSelector.getLabelNames();
    const nextLabelId = labelNames[labelIndex].id;

    switch (labelType) {
      case LabelType.POINT: {
        const point = LabelsSelector.getActivePointLabel();
        if (!point) return imageData;
        imageData.labelPoints = imageData.labelPoints.map(
          (labelPoint: LabelPoint) => {
            if (String(labelPoint.id) === String(point.id)) {
              return {
                ...labelPoint,
                labelId: nextLabelId,
                status: LabelStatus.ACCEPTED,
              };
            }
            return labelPoint;
          }
        );
        store.dispatch(updateActiveLabelId(point.id));
        break;
      }
      case LabelType.LINE: {
        const line = LabelsSelector.getActiveLineLabel();
        if (!line) return imageData;
        imageData.labelLines = imageData.labelLines.map(
          (labelLine: LabelLine) => {
            if (String(labelLine.id) === String(line.id)) {
              return {
                ...labelLine,
                labelId: nextLabelId,
                status: LabelStatus.ACCEPTED,
              };
            }
            return labelLine;
          }
        );
        store.dispatch(updateActiveLabelId(line.id));
        break;
      }
      case LabelType.POLYLINE: {
        const polyLine = LabelsSelector.getActivePolylineLabel();
        if (!polyLine) return imageData;
        imageData.labelPolylines = imageData.labelPolylines.map(
          (labelPolyline: LabelPolyline) => {
            if (String(labelPolyline.id) === String(polyLine.id)) {
              return {
                ...labelPolyline,
                labelId: nextLabelId,
                status: LabelStatus.ACCEPTED,
              };
            }
            return labelPolyline;
          }
        );
        store.dispatch(updateActiveLabelId(polyLine.id));
        break;
      }
      case LabelType.RECT: {
        const rect = LabelsSelector.getActiveRectLabel();
        if (!rect) return imageData;
        imageData.labelRects = imageData.labelRects.map(
          (labelRectangle: LabelRect) => {
            if (String(labelRectangle.id) === String(rect.id)) {
              return {
                ...labelRectangle,
                labelId: nextLabelId,
                status: LabelStatus.ACCEPTED,
              };
            }
            return labelRectangle;
          }
        );
        store.dispatch(updateActiveLabelId(rect.id));
        break;
      }
      case LabelType.POLYGON: {
        const polygon = LabelsSelector.getActivePolygonLabel();
        if (!polygon) return imageData;
        imageData.labelPolygons = imageData.labelPolygons.map(
          (labelPolygon: LabelPolygon) => {
            if (String(labelPolygon.id) === String(polygon.id)) {
              return {
                ...labelPolygon,
                labelId: nextLabelId,
                status: LabelStatus.ACCEPTED,
              };
            }
            return labelPolygon;
          }
        );
        store.dispatch(updateActiveLabelId(polygon.id));
        break;
      }
      case LabelType.TAG: {
        const tagId = this.getTagIdOnIndex(labelIndex)
        if (!tagId) return imageData;
        const existingTag = imageData.labelTags.find((item) => item.labelId === tagId)
        if (existingTag) {
          const data = imageData.labelTags.filter((v) => existingTag.labelId !== v.labelId)
          return {
            ...imageData,
            labelTags: data
          }
        }
        else {
          const temp = { id: uuidv4(), labelId: tagId, isCreatedByAI: false, status: "ACCEPTED", show: true, confidence: 1 }
          imageData.labelTags.push(temp)
          return imageData
        }
      }
    }

    return imageData;
  }
}
